import React, { useState } from 'react';
import { BluetoothIcon, CheckCircle2Icon, Loader2Icon, PrinterIcon, RotateCcwIcon } from 'lucide-react';
import { toast } from 'sonner';
import type { DemoUser } from '../../types/config';
import type { FieldScreen } from '../../types/navigation';
import { useStore } from '../../contexts/StoreContext';
import { MobileHeader } from '../../components/mobile/MobileHeader';
import { BarcodeSticker } from '../../components/print/BarcodeSticker';
import { Modal } from '../../components/shared/Modal';
import { fmtDateTime, nowIso } from '../../utils/bn';
const PRINTERS = [{
  id: 'DMP-PRN-07',
  name: 'পোর্টেবল লেবেল প্রিন্টার DMP-PRN-07',
  battery: '৮২%'
}, {
  id: 'DMP-PRN-12',
  name: 'পোর্টেবল লেবেল প্রিন্টার DMP-PRN-12',
  battery: '৪৫%'
}];
const REASONS = ['স্টিকার ছিঁড়ে গেছে', 'প্রিন্ট অস্পষ্ট', 'ভুল যানবাহনে সংযুক্ত হয়েছিল', 'প্রিন্টারে কাগজ আটকে গেছে'];
type Phase = 'idle' | 'connecting' | 'connected' | 'printing' | 'printed';
export function StickerScreen({
  pcr,
  user,
  go
}: {
  pcr: string;
  user: DemoUser;
  go: (s: FieldScreen) => void;
}) {
  const {
    getCase,
    cases,
    updateCase
  } = useStore();
  const c = getCase(pcr);
  const [printer, setPrinter] = useState(PRINTERS[0].id);
  const [phase, setPhase] = useState<Phase>('idle');
  const [reprintOpen, setReprintOpen] = useState(false);
  const [reason, setReason] = useState(REASONS[0]);
  if (!c) return <p className="p-6" data-id="element-1679" data-mp="3441z0mbi4un">কেস পাওয়া যায়নি।</p>;
  const nextSerial = () => {
    const max = cases.reduce((m, x) => Math.max(m, Number(x.barcode.serial?.slice(4) ?? 0), ...x.barcode.reprints.map(r => Number(r.newSerial.slice(4)))), 0);
    return `STK-${String(max + 1).padStart(6, '0')}`;
  };
  const serial = c.barcode.serial ?? nextSerial();
  const connect = () => {
    setPhase('connecting');
    setTimeout(() => setPhase('connected'), 900);
  };
  const print = (isReprint = false) => {
    setPhase('printing');
    setTimeout(() => {
      if (isReprint && c.barcode.serial) {
        const newSerial = nextSerial();
        updateCase(pcr, x => ({
          ...x,
          barcode: {
            ...x.barcode,
            serial: newSerial,
            printedAt: nowIso(),
            attached: false,
            reprints: [...x.barcode.reprints, {
              at: nowIso(),
              reason,
              oldSerial: x.barcode.serial ?? '',
              newSerial,
              by: user.name
            }]
          }
        }), {
          key: 'reprint',
          actor: user.name,
          role: user.title,
          action: `স্টিকার পুনঃপ্রিন্ট — ${c.barcode.serial} বাতিল, নতুন ${newSerial}`,
          note: reason
        });
      } else {
        updateCase(pcr, x => ({
          ...x,
          barcode: {
            ...x.barcode,
            serial,
            printedAt: nowIso()
          }
        }), {
          key: 'sticker',
          actor: user.name,
          role: user.title,
          action: `বারকোড স্টিকার প্রিন্ট — ${serial}`
        });
      }
      setPhase('printed');
      toast.success('বারকোড স্টিকার সফলভাবে প্রিন্ট হয়েছে।');
    }, 1300);
  };
  const confirmAttached = () => {
    updateCase(pcr, x => ({
      ...x,
      barcode: {
        ...x.barcode,
        attached: true
      }
    }), {
      key: 'attached',
      actor: user.name,
      role: user.title,
      action: 'বারকোড স্টিকার যানবাহনে সংযুক্ত নিশ্চিত'
    });
    toast.success('স্টিকার সংযুক্তি নিশ্চিত হয়েছে।');
    go({
      name: 'pickup',
      pcr
    });
  };
  const connected = phase !== 'idle' && phase !== 'connecting';
  return <>
      <MobileHeader appName="ডিএমপি যানবাহন হেফাজত" title="যানবাহনের বারকোড স্টিকার" onBack={() => go({
      name: 'receipt',
      pcr
    })} data-id="element-1680" data-mp="1y40c862dm6vh" />
      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-3.5" data-id="element-1681" data-mp="233v0s2yoikdd">
        <section data-id="element-1682" data-mp="2qs4iasov0lyp">
          <p className="label" data-id="element-1683" data-mp="2qtpd8jf366x3">প্রিন্ট প্রিভিউ</p>
          <div className="rounded-xl bg-[#E9EEF3] p-4" data-id="element-1684" data-mp="2vrh3l9031kz6">
            <BarcodeSticker pcr={c.pcr} serial={serial} regNo={c.regNo} date={c.createdAt} data-id="element-1685" data-mp="2vq5xv3n2u35f" />
          </div>
          <p className="mt-1.5 text-[11px] text-ink-soft" data-id="element-1686" data-mp="2hr6f2cm8wih6">বারকোডে শুধুমাত্র PCR রেফারেন্স রয়েছে — কোনো ব্যক্তিগত তথ্য নেই।</p>
        </section>

        <section className="card p-4" data-id="element-1687" data-mp="16csqf5jo3z03">
          <p className="label flex items-center gap-1.5" data-id="element-1688" data-mp="16brhb6m7pml0"><BluetoothIcon className="h-4 w-4 text-navy" data-id="element-1689" data-mp="1ae5y7l6bmc9n" /> প্রিন্টার নির্বাচন</p>
          <div className="space-y-1.5" data-id="element-1690" data-mp="1ac1js9aspuv4">
            {PRINTERS.map(p => <label key={p.id} className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-sm ${printer === p.id ? 'border-navy bg-navy-50' : 'border-line'}`} data-id="element-1691" data-mp="1qmvcwkf2tjlh">
                <input type="radio" className="accent-navy" checked={printer === p.id} onChange={() => {
              setPrinter(p.id);
              setPhase('idle');
            }} data-id="element-1692" data-mp="1gupo1jk4fiai" />
                <span className="flex-1" data-id="element-1693" data-mp="1bubcvsn83s89">{p.name}</span>
                <span className="text-[11px] text-ink-muted" data-id="element-1694" data-mp="29j3kvvzh48kh">ব্যাটারি {p.battery}</span>
              </label>)}
          </div>
          <div className="mt-3 flex items-center justify-between rounded-lg bg-canvas px-3 py-2 text-xs" data-id="element-1695" data-mp="24lv3hxm5g8ka">
            <span className={connected ? 'font-semibold text-success' : 'text-ink-muted'} data-id="element-1696" data-mp="1zo1uyxxdj2gu">
              {phase === 'connecting' ? 'সংযুক্ত হচ্ছে…' : connected ? 'ডিভাইস সংযুক্ত (সিমুলেটেড ব্লুটুথ)' : 'ডিভাইস সংযুক্ত নয়'}
            </span>
            {!connected && <button className="btn btn-sm btn-outline" onClick={connect} disabled={phase === 'connecting'} data-id="element-1697" data-mp="k3nciitdiqvn">
                {phase === 'connecting' ? <Loader2Icon className="h-3.5 w-3.5 animate-spin" data-id="element-1698" data-mp="2vqqxi19b5xqm" /> : <BluetoothIcon className="h-3.5 w-3.5" data-id="element-1699" data-mp="2zuyns2vezlqy" />} ডিভাইস সংযুক্ত করুন
              </button>}
          </div>
        </section>

        {phase === 'printed' && <section className="rounded-xl border border-success/30 bg-success-50 p-4" data-id="element-1700" data-mp="39k8cd8r1umab">
            <div className="flex items-center gap-2 text-success" data-id="element-1701" data-mp="3ejl1y19b27gl"><CheckCircle2Icon className="h-5 w-5" data-id="element-1702" data-mp="3eiryb95n7y2o" /><p className="text-sm font-bold" data-id="element-1703" data-mp="3jhwdcgf90q0x">বারকোড স্টিকার সফলভাবে প্রিন্ট হয়েছে।</p></div>
            <p className="mt-3 text-sm font-semibold text-ink" data-id="element-1704" data-mp="3njznn1q6lokm">স্টিকারটি যানবাহনে সংযুক্ত করা হয়েছে কি?</p>
            <div className="mt-2 grid grid-cols-2 gap-2" data-id="element-1705" data-mp="3sjcm6jqf0k31">
              <button className="btn btn-green" onClick={confirmAttached} data-id="element-1706" data-mp="3sgqduhvpa0jr">হ্যাঁ, সংযুক্ত করা হয়েছে</button>
              <button className="btn btn-outline" onClick={() => setReprintOpen(true)} data-id="element-1707" data-mp="1t8337sq5piz2"><RotateCcwIcon className="h-4 w-4" data-id="element-1708" data-mp="1y5bm5vj4pr1d" /> পুনরায় প্রিন্ট করুন</button>
            </div>
          </section>}

        {c.barcode.reprints.length > 0 && <section className="card p-4" data-id="element-1709" data-mp="2whe43ytcmlyr">
            <p className="text-sm font-bold text-ink" data-id="element-1710" data-mp="2wipb07i3c9u3">পুনঃপ্রিন্ট লগ</p>
            <ul className="mt-2 space-y-2" data-id="element-1711" data-mp="3ah6shnhekq3k">
              {c.barcode.reprints.map(r => <li key={r.newSerial} className="text-xs" data-id="element-1712" data-mp="35g9gxtqweub1">
                  <span className="font-mono text-danger line-through" data-id="element-1713" data-mp="3kebixzia77q6">{r.oldSerial}</span> → <span className="font-mono font-semibold text-success" data-id="element-1714" data-mp="3kcgvoejjf3ix">{r.newSerial}</span>
                  <p className="text-ink-muted" data-id="element-1715" data-mp="3fde2brcx6lqs">{r.reason} · {fmtDateTime(r.at)} · {r.by}</p>
                </li>)}
            </ul>
            <p className="mt-2 text-[11px] text-ink-soft" data-id="element-1716" data-mp="3p4rybp5nx3f6">বাতিলকৃত স্টিকার আর বৈধ নয় — স্ক্যান করলে প্রত্যাখ্যাত হবে।</p>
          </section>}
      </div>
      <div className="shrink-0 border-t border-line bg-white p-3" data-id="element-1717" data-mp="6xkib2jdqs4z">
        <button className="btn btn-primary btn-lg w-full" disabled={!connected || phase === 'printing'} onClick={() => c.barcode.serial && phase === 'printed' ? setReprintOpen(true) : print(false)} data-id="element-1718" data-mp="21wtio8t8100">
          {phase === 'printing' ? <><Loader2Icon className="h-4 w-4 animate-spin" data-id="element-1719" data-mp="qje14nu9sjff" /> প্রিন্ট হচ্ছে…</> : <><PrinterIcon className="h-5 w-5" data-id="element-1720" data-mp="bnem0t8qtj0s" /> স্টিকার প্রিন্ট</>}
        </button>
      </div>

      <Modal open={reprintOpen} onClose={() => setReprintOpen(false)} title="পুনঃপ্রিন্টের কারণ" contained footer={<button className="btn btn-primary" onClick={() => {
      setReprintOpen(false);
      print(true);
    }} data-id="element-1722" data-mp="2ub8ym20f86o">পুনঃপ্রিন্ট নিশ্চিত করুন</button>} data-id="element-1721" data-mp="glznevzqe8qt">
        <p className="text-xs text-ink-muted mb-2" data-id="element-1723" data-mp="3p348vdz8uy0k">বর্তমান স্টিকার <b className="font-mono" data-id="element-1724" data-mp="3p4nnz195z30r">{c.barcode.serial}</b> বাতিল হবে এবং নতুন সিরিয়াল ইস্যু হবে। এই কার্যক্রম লগে সংরক্ষিত থাকবে।</p>
        <div className="space-y-1.5" data-id="element-1725" data-mp="3tzul1q7xh9gp">
          {REASONS.map(r => <label key={r} className="flex items-center gap-2 text-sm" data-id="element-1726" data-mp="3g2xuzqzmvw0v"><input type="radio" className="accent-navy" checked={reason === r} onChange={() => setReason(r)} data-id="element-1727" data-mp="3k4j91fnwmgcn" />{r}</label>)}
        </div>
      </Modal>
    </>;
}